'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useForm, FormProvider } from 'react-hook-form'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import { ThankYouMark } from '@/components/forms/ThankYouMark'
import { fields } from '@/blocks/Form/fields'
import { getClientSideURL } from '@/utilities/getURL'
import { extractContactFromFormData, formDataToFields, reportToLeadCentre } from '@/utilities/leadCentre'
import type { LeadCentreLeadType } from '@/utilities/leadCentre'

/** Intent carried by the CTA that opened the modal — recorded on the enquiry. */
export type EnquiryIntent = 'question' | 'offer' | 'apply' | 'inspection'

/**
 * Figma sets the articles in lower case — `Make an Offer` / `Ask a Question`
 * (buttons 4022:84659 / 4022:84660, sold frame 4084:153204, and the modal title
 * 4088:172473). The Residential-Rental frame 4086:155602 is the odd one out and
 * still reads "Book An Inspection" / "Ask A Question"; the lower-case form is
 * used consistently here because it is what the sale + overlay frames specify.
 */
export const INTENT_LABELS: Record<EnquiryIntent, string> = {
  question: 'Ask a Question',
  offer: 'Make an Offer',
  apply: 'Apply',
  inspection: 'Book an Inspection',
}

const MESSAGE_PROMPTS: Record<EnquiryIntent, string> = {
  question: 'What would you like to ask?',
  offer: 'Tell us about your offer.',
  apply: 'Tell us a little about yourself.',
  inspection: 'When would suit you to inspect?',
}

export interface EnquiryAgent {
  name: string
  phone: string | null
  photoUrl: string | null
}

interface EnquiryModalProps {
  open: boolean
  intent: EnquiryIntent
  onClose: () => void
  /** e.g. "10 Fairweather Avenue, Suburbville" */
  addressShort: string
  form: FormType | null
  agents: EnquiryAgent[]
  agentEmails: string[]
  /** FeedBridge listing_vertical, e.g. 'rental' | 'residential' | 'commercial' — used
   *  only to classify the Lead Centre lead_type for the 'question' intent (apply/
   *  inspection are already rental-only by construction). Callers that don't have a
   *  listing in scope (e.g. the staff sidebar "Ask a Question" button) may omit it;
   *  the lead is then reported as a buyer enquiry by default. */
  vertical?: string | null
}

/** Lead Centre lead_type for each enquiry intent — apply/inspection only ever fire
 *  for rental listings (see PropertyCtaButtons); 'question' can come from either a
 *  sale or rental listing, so it falls back on the caller-supplied vertical. */
function leadCentreTypeFor(intent: EnquiryIntent, vertical?: string | null): LeadCentreLeadType {
  switch (intent) {
    case 'apply':
    case 'inspection':
      return 'RENTAL_ENQUIRY'
    case 'offer':
      return 'BUYER_ENQUIRY'
    case 'question':
    default:
      return vertical === 'rental' ? 'RENTAL_ENQUIRY' : 'BUYER_ENQUIRY'
  }
}

/**
 * Centered enquiry modal — Figma `Form Modal` 4088:172470 inside the
 * "Property Detail - Ask a Question" overlay 4088:170029.
 *
 * Panel  368 × 904, SQUARE corners (no radius token is bound), padding/p-6 24.
 *        Header `Frame 696` 24 → body `Frame 591` 24 → Staff Cards `Frame 732`
 *        24 → Form. Staff Card 320 × 80: an 80px square photo, gap/gap-4 16, then
 *        name (Body/bodyMdAlt 16/24 w400) over phone (Body/bodySm 14/16 w300).
 * Scrim  `Pop Up Background Overlay` #FFFFFF + `backdrop-blur/lg` — the page behind
 *        is WHITENED and blurred, not dimmed. Same treatment as the nav overlay.
 */
export function EnquiryModal({
  open,
  intent,
  onClose,
  addressShort,
  form,
  agents,
  agentEmails,
  vertical,
}: EnquiryModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Prevent body scroll while open + focus the close button
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      setTimeout(() => closeRef.current?.focus(), 50)
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  if (!open || !mounted) return null

  const modal = (
    <div
      ref={overlayRef}
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-0 backdrop-blur-lg sm:p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={INTENT_LABELS[intent]}
    >
      {/* Full-screen sheet on mobile; centred 368px card from sm up.
          Asana 021 — 1px theme/border on the card (Figma 4088:170029); the
          mobile full-screen sheet stays borderless. */}
      <div className="bg-background flex h-full max-h-none w-full max-w-none flex-col gap-6 overflow-y-auto p-6 sm:h-auto sm:max-h-[90vh] sm:max-w-[368px] sm:rounded-sm sm:border sm:border-border sm:shadow-md">
        {/* Header `Frame 696` 4088:172471 — title + address, close icon button */}
        <div className="flex items-start gap-1">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {/* Text 4088:172473 binds Headings/headlineSmAlt — Merriweather
                ITALIC, font/size/lg 24 / lineHeight/lg 32 / letterSpacing -0.1. */}
            <p className="font-display text-display-lg text-foreground font-normal italic">
              {INTENT_LABELS[intent]}
            </p>
            {/* Text 4088:172474 is 24 tall inside a 68-tall header (32 + 12 + 24)
                → font/size/base 16 with line-height bound to font/size/lg. */}
            <p className="text-body-md text-foreground">{addressShort}</p>
          </div>
          {/* Figma 4088:170029 measures the close control at 24×24 with a 1px border
              and a ~10px glyph — Icon Button Mini, type outline. */}
          <Button
            ref={closeRef}
            type="button"
            variant="outline"
            size="icon-mini"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0"
          >
            <svg
              className="size-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </Button>
        </div>

        {/* Body `Frame 591` 4088:172476 — Staff Cards then the form, 24 apart. */}
        <div className="flex flex-col gap-6">
          {/* Staff Cards `Frame 732` 4088:172477 — 320×80 rows, gap/gap-6 24 */}
          {agents.length > 0 && (
            <div className="flex flex-col gap-6">
              {agents.map((agent, i) => (
                <div key={i} className="flex items-center gap-4">
                  {/* Loading Ground behind the avatar while it downloads (Steve
                      2026-08-23); the no-photo box keeps the plain muted tint. */}
                  <div
                    className={`size-20 shrink-0 overflow-hidden ${
                      agent.photoUrl ? 'loading-ground' : 'bg-muted'
                    }`}
                  >
                    {agent.photoUrl && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={agent.photoUrl}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {/* get_variable_defs 4088:172478 — name Body/bodyMdAlt 16/24
                        weight 400, phone Body/bodySm 14/16 weight 300. */}
                    <p className="text-body-md text-foreground font-normal">{agent.name}</p>
                    {agent.phone && (
                      <p className="text-body-sm text-foreground font-light">{agent.phone}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Form — keyed by intent so state resets between intents */}
          {form ? (
            <EnquiryForm
              key={intent}
              form={form}
              intent={intent}
              addressShort={addressShort}
              agentEmails={agentEmails}
              vertical={vertical}
            />
          ) : (
            <p className="text-body-sm text-foreground/70">
              {agentEmails.length > 0 ? (
                <>
                  Contact us at{' '}
                  <a
                    className="btn-underline"
                    href={`mailto:${agentEmails[0]}?subject=${encodeURIComponent(`${INTENT_LABELS[intent]} — ${addressShort}`)}`}
                  >
                    {agentEmails[0]}
                  </a>
                </>
              ) : (
                'Please call our office to enquire about this property.'
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

/** Renders the Payload "Property Question" form fields per the Figma modal layout. */
function EnquiryForm({
  form: formFromProps,
  intent,
  addressShort,
  agentEmails,
  vertical,
}: {
  form: FormType
  intent: EnquiryIntent
  addressShort: string
  agentEmails: string[]
  vertical?: string | null
}) {
  const {
    id: formID,
    confirmationMessage,
    confirmationType,
    submitButtonLabel,
    fields: formFields,
  } = formFromProps

  const formMethods = useForm({ defaultValues: formFromProps.fields })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      const submitForm = async () => {
        setError(undefined)

        const dataToSend: { field: string; value: string }[] = Object.entries(data).map(
          ([name, value]) => ({
            field: name,
            value: String(value ?? ''),
          }),
        )

        // Hidden context fields — record the property + intent on the enquiry
        dataToSend.push(
          { field: 'subject', value: `${INTENT_LABELS[intent]} — ${addressShort}` },
          { field: 'enquiryType', value: INTENT_LABELS[intent] },
          { field: 'propertyAddress', value: addressShort },
          { field: 'agentEmails', value: agentEmails.join(', ') },
        )

        setIsLoading(true)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          })

          const res = await req.json()

          if (req.status >= 400) {
            setIsLoading(false)
            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })
            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          const { name, email, phone } = extractContactFromFormData(dataToSend)
          reportToLeadCentre({
            name,
            email,
            phone,
            leadType: leadCentreTypeFor(intent, vertical),
            sourceForm: `property-enquiry-${intent}`,
            fields: { enquiry_type: intent, ...formDataToFields(dataToSend) },
          })
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({ message: 'Something went wrong.' })
        }
      }

      void submitForm()
    },
    [formID, intent, addressShort, agentEmails, vertical],
  )

  if (hasSubmitted && confirmationType === 'message') {
    return (
      /* Asana 018 — shared thank-you mark; display-lg heading scale suits the
         368px modal (the full display-2xl is for page-level thank-yous). */
      <div className="flex flex-col items-start gap-6">
        <ThankYouMark />
        <div className="text-body-md text-foreground [&_h1]:font-display [&_h1]:text-display-lg [&_h1]:font-normal [&_h2]:font-display [&_h2]:text-display-lg [&_h2]:font-normal">
          <RichText data={confirmationMessage} enableGutter={false} />
        </div>
      </div>
    )
  }

  // Split fields: contact details first, textarea (message) prompted separately — Figma 1:11350
  const detailFields = (formFields || []).filter((f) => f.blockType !== 'textarea')
  const messageFields = (formFields || []).filter((f) => f.blockType === 'textarea')

  const renderField = (field: FormFieldBlock, index: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
    if (!Field) return null
    return (
      <div className="w-full" key={index}>
        <Field
          form={formFromProps}
          {...field}
          {...formMethods}
          control={control}
          errors={errors}
          register={register}
        />
      </div>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form
        id={`property-enquiry-${formID}`}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-8"
      >
        {/* Alert copy is Body/bodySm 14/16 (Alert 2198:34205); 13px is off-scale. */}
        {error && <div className="text-body-sm text-destructive">{error.message}</div>}

        {/* Every Body/* token bound inside Form 4088:172480 is weight 300 or 400 —
            there is no 600 anywhere in the Wilson type system. */}
        <div className="flex flex-col gap-6">
          <p className="text-body-md text-foreground font-normal">Please confirm your details.</p>
          <div className="flex flex-col gap-6">{detailFields.map(renderField)}</div>
        </div>

        {messageFields.length > 0 && (
          <div className="flex flex-col gap-6">
            <p className="text-body-md text-foreground font-normal">{MESSAGE_PROMPTS[intent]}</p>
            <div className="flex flex-col gap-6">{messageFields.map(renderField)}</div>
          </div>
        )}

        {/* Figma 4088:170029 measures the submit at 83×36 with the accent fill —
            Button Regular, type primary. `loading` drives the spinner + aria-busy +
            disabled state (replaces the old manual `disabled` + "Sending…" swap). */}
        <Button type="submit" loading={isLoading} className="self-start">
          {submitButtonLabel || 'Send'}
        </Button>
      </form>
    </FormProvider>
  )
}
